import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { ReactComponent as WandMagicSparkles } from 'assets/images/wand-magic-sparkles.svg'
import palette from 'constants/palette'
import { useOrderingErrorMessages } from 'hooks/useOrderingErrorMessages'
import React from 'react'
import './ErrorMessages.scss'
import GeneratingOverlay from './GeneratingOverlay'

const AiGenerationModal: React.FC = () => {
  const [css] = useStyletron()
  const {
    aiModal,
    updateBrandTone,
    closeAiModal,
    handleGenerateAi,
    handleSaveTone: saveTone,
    isSavingTone,
    isGenerating,
  } = useOrderingErrorMessages()

  const { isOpen, brandTone } = aiModal

  const handleGenerate = () => {
    if (brandTone.trim()) {
      handleGenerateAi(brandTone.trim())
    }
  }

  const handleSaveTone = () => {
    if (brandTone.trim()) {
      saveTone(brandTone.trim())
    }
  }

  const isBusy = isGenerating || isSavingTone
  const isGenerateDisabled = !brandTone.trim() || isBusy

  return isOpen ? (
    <>
      {/* Backdrop */}
      <div
        className={css({
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        })}
        onClick={isBusy ? undefined : closeAiModal}
      />

      {/* Modal */}
      <div
        className={css({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          width: '520px',
          maxWidth: '90vw',
          zIndex: 1001,
          boxShadow:
            '0px 4px 8px rgba(54, 66, 78, 0.08), 0px 8px 24px rgba(54, 66, 78, 0.12)',
        })}
      >
        {isGenerating && <GeneratingOverlay />}

        {/* Header */}
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '48px 24px 24px 24px',
            position: 'relative',
          })}
        >
          <WandMagicSparkles
            className={css({ width: '24px', height: '24px', flexShrink: 0 })}
          />
          <Text.Header4 className="m-0">Generate with ThanxAI</Text.Header4>
          <button
            data-testid="close-modal-button"
            aria-label="Close modal"
            onClick={closeAiModal}
            disabled={isBusy}
            className={css({
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              cursor: isBusy ? 'not-allowed' : 'pointer',
              fontSize: '20px',
              color: '#757575',
              opacity: isBusy ? 0.5 : 1,
              borderRadius: '4px',
            })}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className={css({ padding: '0 24px' })}>
          {/* Description */}
          <Text.BodyText4
            color="grey70"
            className={css({ marginBottom: '16px' })}
          >
            Tell us about your brand and we'll customize all error messages to
            match your voice and tone.
          </Text.BodyText4>

          {/* Brand tone input */}
          <textarea
            data-testid="brand-tone-input"
            value={brandTone}
            onChange={e => updateBrandTone(e.target.value)}
            disabled={isBusy}
            placeholder="e.g., friendly and casual, professional but approachable, fun and playful..."
            className={css({
              width: '100%',
              minHeight: '100px',
              padding: '12px',
              borderRadius: '4px',
              border: `1px solid ${palette.grey40}`,
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
              ':focus': {
                outline: 'none',
                borderColor: palette.belizeHole,
              },
              ':disabled': {
                backgroundColor: '#F5F5F5',
                cursor: 'not-allowed',
              },
            })}
          />
        </div>

        {/* Footer */}
        <div
          className={css({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            padding: '24px 24px 48px 24px',
          })}
        >
          <Button
            kind={Kind.SECONDARY}
            data-testid="save-tone-button"
            onClick={handleSaveTone}
            disabled={!brandTone.trim() || isBusy}
            isLoading={isSavingTone}
          >
            Save tone
          </Button>
          <Button
            data-testid="generate-ai-button"
            onClick={handleGenerate}
            disabled={isGenerateDisabled}
            isLoading={isGenerating}
          >
            Generate AI suggestions
          </Button>
        </div>
      </div>
    </>
  ) : null
}

export default AiGenerationModal
